import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Box,
  useTheme,
  useMediaQuery,
  Button,
  Card,
} from '@mui/material';
import { Menu as MenuIcon, Logout as LogoutIcon, Inventory as InventoryIcon, SwapHoriz as TransferIcon } from '@mui/icons-material';
import { FolderKanban, ClipboardList } from 'lucide-react';
// Custom SVG icons
import { ReactComponent as SparklesIcon } from '../assets/icons/sparkles.svg';
import chartIcon from '../assets/icons/chart.svg';
import clipboardIcon from '../assets/icons/Clipboard.svg';
import { ReactComponent as ClipboardSvg } from '../assets/icons/Clipboard.svg';
import locationOnIcon from '../assets/icons/location_on.svg';
import creditCardIcon from '../assets/icons/Credit card.svg';
import settings2Icon from '../assets/icons/settings-2.svg';

const drawerWidth = 240;

const ClientNavigation = ({ children, currentUser, onLogout }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    onLogout();
  };

  const menuItems = [
    { text: 'Overview', iconSrc: chartIcon, path: '/client/dashboard' },
    { text: 'Order History', iconComponent: ClipboardList, path: '/client/orders' },
    { text: 'Transfers', icon: <TransferIcon />, path: '/client/transfers' },
    { text: 'Inventory Management', icon: <InventoryIcon />, path: '/client/inventory-management' },
    { text: 'My Addresses', iconSrc: locationOnIcon, path: '/client/my-shipping-address' },
    // { text: 'My Payment Methods', iconSrc: creditCardIcon, path: '/client/my-payments' }, // Hidden - payment methods managed by Tricera
    { text: 'Resources', iconComponent: FolderKanban, path: '/client/resources' },
    { text: 'Settings', iconSrc: settings2Icon, path: '/client/settings' }
  ];

  const supportItems = [];

  const accountItems = [
    { text: 'Sign Out', icon: <LogoutIcon />, action: handleLogout }
  ];

  const drawer = (
    <Box sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      bgcolor: '#f7f7f8',
      borderRight: '1px solid #ececef',
      fontFamily: 'Poppins, sans-serif'
    }}>
      {/* Top Banner and Order Button */}
      <Box sx={{ p: 2, pt: 0, mb: 2 }}>
        <Box
          sx={{
            // make the grey bar span edge-to-edge inside the drawer
            mx: -2,                 // cancel the parent’s horizontal padding
            width: 'auto',          // let it fill after the negative margin
            height: 56,
            bgcolor: '#D9D9D9',
            borderBottom: '1px solid #ececef',
            borderTopLeftRadius: 0, // no inner rounding when full-bleed
            borderTopRightRadius: 0
          }}
        />
        <Box sx={{ pt: 2 }}>
          <Button
            onClick={() => {
              navigate('/client/place-order');
              if (isMobile) setMobileOpen(false);
            }}
            sx={{
              width: '100%',
              bgcolor: '#ccfbf1',
              color: '#039582',
              borderRadius: '6px',
              py: '6px',
              px: '10px',
              textTransform: 'none',
              fontFamily: 'Poppins',
              fontSize: '14px',
              fontWeight: 500,
              display: 'flex',
              gap: '6px',
              justifyContent: 'flex-start',
              alignItems: 'center',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',

              '&:hover': {
                bgcolor: '#ccfbf1',
                opacity: 0.8
              }
            }}
          >
            <Box sx={{
              width: 24,
              height: 24,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#039582',
              '& path, & polygon, & circle, & rect, & line, & polyline': {
                fill: 'none',
                stroke: 'currentColor',
                strokeWidth: 1.6,
                strokeLinejoin: 'round',
                strokeLinecap: 'round'
              }
            }}>
              <SparklesIcon />
            </Box>
            Order Now
          </Button>
        </Box>
      </Box>

      {/* Navigation Menu */}
      <Box sx={{ px: 2, flexGrow: 1 }}>
        <List sx={{ p: 0 }}>
          {menuItems.map((item) => (
            <ListItem key={item.text} disablePadding sx={{ mb: '4px' }}>
              <ListItemButton
                onClick={() => {
                  navigate(item.path);
                  if (isMobile) setMobileOpen(false);
                }}
                sx={{
                  borderRadius: '6px',
                  backgroundColor: location.pathname === item.path
                    ? '#e5e7eb'
                    : 'transparent',
                  color: '#212121',
                  '&:hover': {
                    backgroundColor: location.pathname === item.path ? '#e5e7eb' : '#f7f7f8'
                  },
                  py: '8px',
                  px: '8px',
                  gap: '4px',
                  minHeight: '36px'
                }}
              >
                <ListItemIcon
                  sx={{
                    color: '#475569',
                    minWidth: 'auto',
                    mr: '8px',
                    '& svg': { width: location.pathname === item.path ? 24 : 18, height: location.pathname === item.path ? 24 : 18 },
                    '& svg *': {
                      fill: 'none',
                      stroke: '#475569',
                      strokeWidth: 1.6,
                      strokeLinejoin: 'round',
                      strokeLinecap: 'round',
                      opacity: 1
                    },
                    // Material-UI icons use fill, not stroke
                    '& .MuiSvgIcon-root': {
                      fill: '#475569',
                      fontSize: location.pathname === item.path ? 24 : 18,
                      '& *': {
                        fill: '#475569',
                        stroke: 'none'
                      }
                    }
                  }}
                >
                  {item.iconComponent ? (
                    <item.iconComponent 
                      size={location.pathname === item.path ? 24 : 18} 
                      color="#475569"
                      strokeWidth={1.6}
                    />
                  ) : item.icon ? (
                    item.icon
                  ) : item.iconSvg ? (
                    <item.iconSvg />
                  ) : (
                    <Box component="img" src={item.iconSrc} alt="" sx={{ width: location.pathname === item.path ? 24 : 18, height: location.pathname === item.path ? 24 : 18, opacity: 1 }} />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontFamily: 'Poppins',
                    fontSize: '15px',
                    fontWeight: 400,
                    lineHeight: '20px',
                    color: '#475569',
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>

      </Box>

      {/* Help Section (moved above Account) */}
      <Box sx={{ p: 2 }}>
        <Card sx={{
          bgcolor: 'white',
          borderRadius: '6px',
          border: '1px solid #ececef',
          boxShadow: 'none',
          p: 2
        }}>
          <Box sx={{ mb: 2 }}>
            <Typography sx={{
              fontFamily: 'Poppins',
              fontSize: '15px',
              fontWeight: 700,
              color: '#212121',
              lineHeight: '20px',
              mb: '4px'
            }}>
              Need Help?
            </Typography>
            <Typography sx={{
              fontFamily: 'Poppins',
              fontSize: '12px',
              fontWeight: 400,
              color: '#212121',
              lineHeight: '18px'
            }}>
              Can't find something? We are always here to help.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: '8px' }}>
            <Button
              variant="outlined"
              onClick={() => window.open('https://help.lipoderma.com', '_blank')}
              sx={{
                fontFamily: 'Poppins',
                fontSize: '12px',
                fontWeight: 500,
                color: '#212121',
                borderColor: '#ececef',
                borderRadius: '6px',
                textTransform: 'none',
                py: '6px',
                px: '12px',
                bgcolor: 'white',
                '&:hover': {
                  borderColor: '#ececef',
                  bgcolor: 'white',
                  opacity: 0.8
                }
              }}
            >
              Help Center
            </Button>
            <Button
              onClick={() => window.open('mailto:support@lipoderma.com')}
              sx={{
                fontFamily: 'Poppins',
                fontSize: '12px',
                fontWeight: 500,
                color: '#212121',
                borderRadius: '6px',
                textTransform: 'none',
                py: '6px',
                px: '4px',
                minWidth: 'auto',
                '&:hover': {
                  bgcolor: 'transparent',
                  opacity: 0.8
                }
              }}
            >
              Contact Us
            </Button>
          </Box>
        </Card>
      </Box>

      {/* Account Actions now at the very bottom */}
      <Box sx={{ p: 2 }}>
        <List sx={{ p: 0 }}>
          {accountItems.map((item) => (
            <ListItem key={item.text} disablePadding sx={{ mb: '4px' }}>
              <ListItemButton
                onClick={item.action}
                sx={{
                  borderRadius: '6px',
                  color: '#dc2626',
                  '&:hover': {
                    backgroundColor: '#fee2e2'
                  },
                  py: '8px',
                  px: '8px',
                  gap: '4px',
                  minHeight: '36px'
                }}
              >
                <ListItemIcon
                  sx={{
                    color: '#dc2626',
                    minWidth: 'auto',
                    mr: '4px',
                    '& .MuiSvgIcon-root': {
                      fontSize: 18
                    }
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontFamily: 'Poppins',
                    fontSize: '15px',
                    fontWeight: 500,
                    lineHeight: '20px',
                    color: '#dc2626'
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>
    </Box>
  );


  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>

      {/* Navigation Drawer */}
      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0
            }
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              position: 'fixed',
              top: 0,
              left: 0,
              height: '100vh',
              zIndex: 1200,
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0
            }
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Mobile Menu Button */}
      <IconButton
        color="inherit"
        aria-label="open drawer"
        edge="start"
        onClick={handleDrawerToggle}
        sx={{
          position: 'fixed',
          top: 20,
          left: 20,
          zIndex: 1300,
          display: { md: 'none' },
          bgcolor: '#039582',
          color: 'white',
          '&:hover': { bgcolor: '#027A6B' }
        }}
      >
        <MenuIcon />
      </IconButton>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          backgroundColor: theme.palette.background.default,
          minHeight: '100vh'
        }}
      >
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default ClientNavigation; 